import React from "react";

/**
 * What the scleral and corneal views both read from a stored result.
 *
 * Fields are taken from an explicit allowlist rather than by iterating the
 * stored object. Results written before de-identification still carry a
 * "file" key holding the original Pentacam export name, which is
 * LASTNAME_FIRSTNAME_EYE_DATE -- iterating would put that on screen.
 */

/**
 * How each verdict is shown. The two rules in the fitting guide are kept
 * visually distinct: a lens the HVID band points at is not the same claim as
 * one whose limbal drill dots land correctly, and a fitter choosing between
 * them should see which is which.
 */
export const SELECTION_STYLE = {
  recommended: { slug: "both", label: "HVID + limbal" },
  "indicated by drill dots": { slug: "limbal", label: "limbal eval" },
  "indicated by HVID": { slug: "hvid", label: "HVID only" },
  "not indicated": { slug: "no", label: "not indicated" },
  unknown: { slug: "unknown", label: "—" },
};

export const num = (v, digits = 1) =>
  typeof v === "number" && Number.isFinite(v) ? v.toFixed(digits) : null;

/**
 * Lens sag is made in 10 µm steps. The backend already rounds it, but results
 * stored before that carry the raw figure, so round again here.
 */
export const sagNum = (v) =>
  typeof v === "number" && Number.isFinite(v) ? (Math.round(v / 10) * 10).toFixed(0) : null;

/** An order's sag -- {"Lens Sag": n} or {"Bi-Elevation": {...}} -- for display. */
export const sagOf = (entry) => {
  if (!entry) return null;
  const be = entry["Bi-Elevation"];
  if (be) {
    return (
      <>
        {sagNum(be.flat_meridian_sag)} <span className="srd-tag">flat</span>
        {"  /  "}
        {sagNum(be.steep_meridian_sag)} <span className="srd-tag">steep</span>
      </>
    );
  }
  return sagNum(entry["Lens Sag"]);
};

/** The same sag as a plain string, to tell whether two orders differ. */
export const sagKey = (entry) => {
  if (!entry) return null;
  const be = entry["Bi-Elevation"];
  if (be) return `${sagNum(be.flat_meridian_sag)}/${sagNum(be.steep_meridian_sag)}`;
  return sagNum(entry["Lens Sag"]);
};

/**
 * What an order carried into its bi-elevation, or could not make. Results
 * saved before flattening was carried say so with "flat overflow unresolved".
 */
export function overflowNotes(limits, order) {
  if (!limits) return [];
  const notes = [];
  if (limits["steep overflow um"] > 0) {
    notes.push(`${limits["steep overflow um"]} µm of steepening exceeded what the lens can be made with, and is carried by the bi-elevation instead.`);
  }
  if (limits["flat overflow unresolved"]) {
    notes.push(`${limits["flat overflow um"]} µm of flattening exceeded the maximum and could not be accommodated — this quadrant will not be as flat as calculated.`);
  } else if (limits["flat overflow um"] > 0) {
    notes.push(`${limits["flat overflow um"]} µm of flattening exceeded what the lens can be made with, and is carried by the bi-elevation instead.`);
  }
  if (limits["unresolved um"] > 0) {
    notes.push(`${limits["unresolved um"]} µm could not be made either way: one meridian's steps are spread wider than the limits allow.`);
  }
  return notes.map((text) => (order ? `${order}: ${text}` : text));
}

/** Merge the per-file entries (corneo-scleral + elevation) into one view. */
export function collect(parameters) {
  const entries = Array.isArray(parameters) ? parameters : [parameters];
  const merged = {
    scanTypes: [],
    device: null,
    eye: null,
    bfs: null,
    hwtw: null,
    diameter: null,
    sag: null,
    biElevation: null,
    baseCurve: null,
    rotation: null,
    toricity: null,
    quadrants: null,
    opticZone: null,
    clearance: null,
    options: null,
    simulatedFit: null,
  };

  entries.filter(Boolean).forEach((e) => {
    if (e["Scan Type"]) merged.scanTypes.push(e["Scan Type"]);
    if (e["Device"] && !merged.device) merged.device = e["Device"];
    if (e["Eye"] && !merged.eye) merged.eye = e["Eye"];
    if (typeof e["BFS"] === "number") merged.bfs = e["BFS"];
    if (typeof e["HWTW"] === "number") merged.hwtw = e["HWTW"];
    if (typeof e["Central Clearance"] === "number") merged.clearance = e["Central Clearance"];
    if (Array.isArray(e["Lens Diameter"]) && merged.diameter == null)
      merged.diameter = e["Lens Diameter"][0];
    if (typeof e["Lens Sag"] === "number") merged.sag = e["Lens Sag"];
    if (e["Bi-Elevation"]) {
      merged.biElevation = e["Bi-Elevation"];
      merged.biElevationReason = e["Bi-Elevation"].reason || null;
    }
    if (e["Step Limits"]) merged.limits = e["Step Limits"];
    if (typeof e["Base Curve"] === "number") merged.baseCurve = e["Base Curve"];
    if (typeof e["Optic Zone Diameter"] === "number")
      merged.opticZone = e["Optic Zone Diameter"];
    if (e["Lens Rotation"]) merged.rotation = e["Lens Rotation"];
    if (e["Lens Toricity"]) merged.toricity = e["Lens Toricity"];
    if (Array.isArray(e["Quadrant Steps"])) merged.quadrants = e["Quadrant Steps"];
    if (Array.isArray(e["Diameter Options"])) merged.options = e["Diameter Options"];
    if (e["Diameter Selection"]) merged.diameterSelection = e["Diameter Selection"];
    if (e["Simulated Fit"]) merged.simulatedFit = e["Simulated Fit"];
  });

  return merged;
}

/** Overlay a chosen diameter's numbers onto the recommended ones. */
export function withDiameter(base, option) {
  if (!option) return base;
  return {
    ...base,
    diameter: option["Lens Diameter"],
    sag: typeof option["Lens Sag"] === "number" ? option["Lens Sag"] : null,
    // each diameter has its own, over its own midperipheral zone; results
    // saved before that carry only the top-level one
    baseCurve: typeof option["Base Curve"] === "number" ? option["Base Curve"] : base.baseCurve,
    midClearance: typeof option["Midperipheral Clearance"] === "number"
      ? option["Midperipheral Clearance"] : null,
    biElevation: option["Bi-Elevation"] || null,
    rotation: option["Lens Rotation"] || null,
    toricity: option["Lens Toricity"] || null,
    quadrants: option["Quadrant Steps"] || null,
    coverage: option["Ring Coverage"],
    clearance: option["Central Clearance"],
    limits: option["Step Limits"] || null,
    biElevationReason: (option["Bi-Elevation"] || {}).reason || null,
    recommended: option["Recommended"],
  };
}

